import type { Identifier, XYCoord } from "dnd-core";
import type { FC } from "react";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import Icon from "../Icon/Icon";
import { ItemTypes, DragItem } from "../ExecutionDragCard/ExecutionDragCard";
import styled, { useTheme } from "styled-components";
// import { Collapsible } from "../StickyCard/StickyCard";
import Collapsible from "../Collapsible/Collapsible";
import { SquareButton } from "../Button/Button";
import { FieldArray, FieldArrayRenderProps, useFormikContext } from "formik";
import TextField from "../FormFields/TextField";
import CheckboxField from "../FormFields/CheckboxField";
import DropdownField from "../FormFields/DropdownField";
import { ROUNDS_KEY } from "../../views/Admin/Judging/JudgingConfig/RoundsTab";
import { RoundBankCard } from "../../views/Admin/Judging/JudgingConfig/RoundsTab";
import {
	JudgingCriterion,
	RoundStructureConfig,
	RoundStructureCriterionConfig,
} from "../../views/Admin/Judging/JudgingInterfaces";
import {
	MULTIVARIABLE_ROUND_NAME,
	isMultivariableRound,
} from "../../views/Admin/Judging/judgingRoundUtils";

const CriteriaSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin: 1rem 0 0 33px;
	width: calc(100% - 33px);

	.criteria-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 1rem;
	}

	.criteria-row {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 120px auto;
		gap: 0.75rem;
		align-items: center;
	}

	.criteria-actions {
		display: flex;
		gap: 0.5rem;
	}

	.criteria-summary {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 1rem;
		font-size: ${({ theme }) => theme.xSmallSize};
		color: ${({ theme }) => theme.colorCopyLight};
	}

	.criteria-note {
		font-size: ${({ theme }) => theme.xxSmallSize};
		color: ${({ theme }) => theme.colorCopyLight};
	}
`;

const RoundDragCard: FC<RoundCardProps> = ({
	id,
	round,
	index,
	handleRemove,
	handleExpand,
	moveRoundCard,
	arrayHelpers,
	criteria,
	disabled,
}) => {
	const { setFieldValue } = useFormikContext<any>();
	const theme = useTheme();

	const ref = useRef<HTMLDivElement>(null);
	const [{ handlerId }, drop] = useDrop<
		DragItem,
		void,
		{ handlerId: Identifier | null }
	>({
		accept: ItemTypes.CARD,
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},
		hover(item: DragItem, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;

			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}

			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();

			// Get vertical middle
			const hoverMiddleY =
				(hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

			// Determine mouse position
			const clientOffset = monitor.getClientOffset();

			// Get pixels to the top
			const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%

			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}

			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}

			// Time to actually perform the action
			moveRoundCard(arrayHelpers, dragIndex, hoverIndex);

			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag] = useDrag({
		type: ItemTypes.CARD,
		item: () => {
			return { id, index };
		},
		collect: (monitor: any) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const opacity = isDragging ? 0 : 1;
	drag(drop(ref));

	const roundStructureCriteria =
		round.roundStructureCriteria
			?.slice()
			.sort((a, b) => a.order - b.order) || [];

	const totalWeight = roundStructureCriteria.reduce(
		(total, criterion) => total + (Number(criterion.weight) || 0),
		0
	);

	const reorderCriteria = (criteriaList: RoundStructureCriterionConfig[]) =>
		criteriaList.map((criterion, criterionIndex) => ({
			...criterion,
			order: criterionIndex + 1,
		}));

	const setRoundStructureCriteria = (
		criteriaList: RoundStructureCriterionConfig[]
	) => {
		setFieldValue(
			`${ROUNDS_KEY}.${index}.roundStructureCriteria`,
			reorderCriteria(criteriaList)
		);
	};

	const addCriterion = () => {
		if (disabled) {
			return;
		}

		const availableCriterion = criteria.find(
			(criterion) =>
				!roundStructureCriteria.some(
					(roundCriterion) => roundCriterion.criterionId === criterion.id
				)
		);

		if (!availableCriterion) {
			return;
		}

		setRoundStructureCriteria([
			...roundStructureCriteria,
			{
				id: 0,
				roundStructureId: round.id,
				criterionId: availableCriterion.id,
				criterionName: availableCriterion.name,
				weight: 0,
				order: roundStructureCriteria.length + 1,
			},
		]);
	};

	const removeCriterion = (criterionIndex: number) => {
		if (disabled) {
			return;
		}

		setRoundStructureCriteria(
			roundStructureCriteria.filter((_, currentIndex) => currentIndex !== criterionIndex)
		);
	};

	const moveCriterion = (criterionIndex: number, offset: number) => {
		if (disabled) {
			return;
		}

		const nextIndex = criterionIndex + offset;

		if (nextIndex < 0 || nextIndex >= roundStructureCriteria.length) {
			return;
		}

		const updatedCriteria = [...roundStructureCriteria];
		const [criterion] = updatedCriteria.splice(criterionIndex, 1);
		updatedCriteria.splice(nextIndex, 0, criterion);
		setRoundStructureCriteria(updatedCriteria);
	};

	const updateCriterion = (
		criterionIndex: number,
		updatedCriterion: Partial<RoundStructureCriterionConfig>
	) => {
		if (disabled) {
			return;
		}

		setRoundStructureCriteria(
			roundStructureCriteria.map((criterion, currentIndex) =>
				currentIndex === criterionIndex
					? { ...criterion, ...updatedCriterion }
					: criterion
			)
		);
	};

	return (
		<div
			className="w-full"
			ref={ref}
			style={{ opacity }}
			data-handler-id={handlerId}
		>
			<RoundBankCard className="flex flex-col w-full">
				<div className="flex justify-between items-center w-full gap-[1rem]">
					<Icon icon="info" color={theme.colorCopyLight} />
					<p>{round.name}</p>
					{/* <p>Round {index + 1}</p> */}
					<Icon
						icon="drag-arrows"
						color={theme.colorPrimary}
						className="flex items-center cursor-pointer ml-auto"
					/>
					<Icon
						icon="expandCaret"
						color={theme.colorPrimary}
						height="20px"
						width="20px"
						rotation={round.isCollapsed ? "0" : "180deg"}
						onClick={() =>
							handleExpand(
								`${ROUNDS_KEY}.${index}.isCollapsed`,
								round.isCollapsed
							)
						}
					/>

					<SquareButton
						disabled={disabled}
						onClick={() => handleRemove(index)}
						icon="close"
						iconRight
						iconSize="21px"
					/>
					{/* <Icon
            icon="closeLarge"
            color={theme.colorPrimary}
            onClick={() => handleRemove(round.id, round.index!)}
          /> */}
				</div>

				<Collapsible className="w-full" isCollapsed={round.isCollapsed}>
					<div className="flex gap-[1rem]">
						<FieldArray name={ROUNDS_KEY} validateOnChange={false}>
							{() => (
								<>
									<TextField
										className="ml-[33px] mt-[1rem] flex-1"
										name={`${ROUNDS_KEY}.${index}.jurorInstructions`}
										placeholder="Juror instructions"
										component="textarea"
										height="80px"
										readOnly={disabled}
										value={round.jurorInstructions}
									/>
									<CheckboxField
										className="mt-[1rem]"
										name={`${ROUNDS_KEY}.${index}.isFeedbackRequired`}
										placeholder="Require Feedback"
										disabled={disabled}
										checked={round.isFeedbackRequired}
									/>
								</>
							)}
						</FieldArray>
					</div>
					{isMultivariableRound(round.name) && (
						<CriteriaSection>
							<div className="criteria-header">
								<div>
									<p className="font-semibold">{MULTIVARIABLE_ROUND_NAME}</p>
									<p className="criteria-note">
										Add weighted criteria. The total must equal 100 before the
										round can be finalized.
									</p>
								</div>
								<ButtonRow>
									<SquareButton
										disabled={
											disabled ||
											roundStructureCriteria.length >= criteria.length
										}
										onClick={addCriterion}
										icon="plus"
									/>
								</ButtonRow>
							</div>

							{roundStructureCriteria.length > 0 ? (
								roundStructureCriteria.map((criterion, criterionIndex) => {
									const criterionOptions = criteria
										.filter(
											(option) =>
												option.id === criterion.criterionId ||
												!roundStructureCriteria.some(
													(roundCriterion) =>
														roundCriterion.criterionId === option.id
												)
										)
										.map((option) => ({
											label: option.name,
											value: option.id,
										}));

									return (
										<div
											className="criteria-row"
											key={`${round.id}.criterion.${criterionIndex}`}
										>
											<DropdownField
												name={`${ROUNDS_KEY}.${index}.roundStructureCriteria.${criterionIndex}.criterionId`}
												value={criterion.criterionId}
												options={criterionOptions}
												disabled={disabled}
												placeholder="Criterion"
												onChange={(event) => {
													const selectedCriterion = criteria.find(
														(option) =>
															option.id === Number(event.currentTarget.value)
													);

													if (selectedCriterion) {
														updateCriterion(criterionIndex, {
															criterionId: selectedCriterion.id,
															criterionName: selectedCriterion.name,
														});
													}
												}}
											/>
											<TextField
												name={`${ROUNDS_KEY}.${index}.roundStructureCriteria.${criterionIndex}.weight`}
												type="number"
												placeholder="Weight %"
												readOnly={disabled}
												value={criterion.weight}
											/>
											<div className="criteria-actions">
												<SquareButton
													disabled={disabled || criterionIndex === 0}
													onClick={() => moveCriterion(criterionIndex, -1)}
													icon="caret"
													iconRotation="180deg"
												/>
												<SquareButton
													disabled={
														disabled ||
														criterionIndex === roundStructureCriteria.length - 1
													}
													onClick={() => moveCriterion(criterionIndex, 1)}
													icon="caret"
												/>
												<SquareButton
													disabled={disabled}
													onClick={() => removeCriterion(criterionIndex)}
													icon="close"
													iconRight
												/>
											</div>
										</div>
									);
								})
							) : (
								<p className="criteria-note">
									No criteria added yet. Start with Creativity,
									Effectiveness, Strategy, and Execution.
								</p>
							)}

							<div className="criteria-summary">
								<span>Total Weight</span>
								<b
									style={{
										color:
											totalWeight === 100
												? theme.colorSuccess
												: theme.colorDanger,
									}}
								>
									{totalWeight}%
								</b>
							</div>
						</CriteriaSection>
					)}
				</Collapsible>
			</RoundBankCard>
		</div>
	);
};

const ButtonRow = styled.div`
	display: flex;
	gap: 0.5rem;
`;

export default RoundDragCard;

interface RoundCardProps {
	id: number;
	round: RoundStructureConfig;
	index: number;
	moveRoundCard: (
		arrayHelpers: FieldArrayRenderProps,
		from: number,
		to: number
	) => void;
	handleRemove: (index: number) => void;
	handleExpand: (name: string, currentValue: boolean) => void;
	arrayHelpers: FieldArrayRenderProps;
	criteria: JudgingCriterion[];
	disabled: boolean;
}
